import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  FolderOpen, Plus, Search, ChevronDown, ChevronRight,
  MoreVertical, Star, Image, MessageSquareText, Globe,
  BarChart3, Video, Music, Clock, FileText, AlignLeft,
  Edit, Copy, Trash2, FolderInput, Eye, Play, Pencil,
  Presentation, ScrollText, Share2, ShoppingBag, CreditCard,
  GripVertical, Mail, Gavel, FileSignature, FolderHeart, X, AlertTriangle,
  Lock, Library, Download
} from 'lucide-react';
import { useSiteContext } from '../../../context/SiteContext';

const ELEMENT_ICONS = {
  cta: Image,
  talking_point: MessageSquareText,
  talkingpoint: MessageSquareText,
  web_overlay: Globe,
  weboverlay: Globe,
  poll: BarChart3,
  video: Video,
  audio: Music,
  timer: Clock,
  presentation: Presentation,
  prompter: AlignLeft,
  product_showcase: ShoppingBag,
  productshowcase: ShoppingBag,
  checkout: CreditCard,
  lead_capture: Mail,
  leadcapture: Mail,
  auction: Gavel,
  contract: FileSignature,
  document: FileText,
  website: Globe,
};

const ELEMENT_COLORS = {
  cta: 'text-pink-400',
  talking_point: 'text-purple-400',
  talkingpoint: 'text-purple-400',
  web_overlay: 'text-blue-400',
  weboverlay: 'text-blue-400',
  poll: 'text-amber-400',
  video: 'text-red-400',
  audio: 'text-green-400',
  timer: 'text-cyan-400',
  presentation: 'text-orange-400',
  prompter: 'text-teal-400',
  product_showcase: 'text-emerald-400',
  productshowcase: 'text-emerald-400',
  checkout: 'text-violet-400',
  lead_capture: 'text-emerald-400',
  leadcapture: 'text-emerald-400',
  auction: 'text-fuchsia-400',
  contract: 'text-indigo-400',
  document: 'text-orange-400',
  website: 'text-blue-400',
};

const ELEMENT_LABELS = {
  video: 'Video',
  audio: 'Audio',
  presentation: 'Presentation',
  cta: 'CTA Banner',
  talking_point: 'Talking Point',
  web_overlay: 'Web Overlay',
  poll: 'Poll',
  timer: 'Timer',
  prompter: 'Prompter',
  product_showcase: 'Product Showcase',
  checkout: 'Checkout',
  lead_capture: 'Lead Capture',
  auction: 'Auction',
  contract: 'Contract',
  document: 'Document',
  website: 'Website',
};

const BASIC_PLAN_TYPES = ['video', 'audio', 'presentation'];

// Hidden element types - temporarily hidden from the "Add Element" dropdown.
// Auction is gated to admin-only via hiddenTypesExtra (see ElementsTab + StudioLeftSidebar)
// while the feature is still in testing — same pattern as Product Showcase.
const HIDDEN_ELEMENT_TYPES = [
  'checkout',
  'document',      // Document
  'website',       // Website
];

// Context for dropdown close function
const DropdownContext = React.createContext(null);
// Registry of submenu portal refs so parent dropdown can exclude them from "click outside"
const SubMenuRefsContext = React.createContext({ refs: [] });

// Custom Dropdown Component (replacing shadcn) - uses portal to escape overflow
const Dropdown = ({ trigger, children, align = 'end' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const subMenuRefsStore = useRef({ refs: [] });

  const closeDropdown = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(event.target);
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(event.target);
      // Check if click is inside any submenu portal
      const isInsideSubMenu = subMenuRefsStore.current.refs.some(
        ref => ref.current && ref.current.contains(event.target)
      );

      if (isOutsideTrigger && isOutsideDropdown && !isInsideSubMenu) {
        setIsOpen(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const calculatePosition = () => {
    if (!triggerRef.current) return { top: 0, left: 0 };

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuHeight = dropdownRef.current ? dropdownRef.current.offsetHeight : 300;
    const padding = 10;

    // Calculate left position
    let left = align === 'end' ? rect.right - 180 : rect.left;
    left = Math.max(padding, Math.min(left, viewportWidth - 190));

    // Calculate top position - prefer below, flip above if no space
    let top = rect.bottom + 4;
    if (top + menuHeight + padding > viewportHeight) {
      // Try above the trigger
      top = rect.top - menuHeight - 4;
    }
    // Clamp to stay within viewport
    top = Math.max(padding, Math.min(top, viewportHeight - menuHeight - padding));

    return { top, left };
  };

  // Reposition after dropdown renders so we can measure actual height
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const frame = requestAnimationFrame(() => {
        setPosition(calculatePosition());
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [isOpen, children]);

  useEffect(() => {
    if (isOpen) {
      const updatePosition = () => {
        setPosition(calculatePosition());
      };
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, align]);

  const handleTriggerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isOpen) {
      setPosition(calculatePosition());
    }
    setIsOpen(prev => !prev);
  };

  return (
    <div className="relative inline-block" ref={triggerRef}>
      <div onClick={handleTriggerClick} style={{ cursor: 'pointer' }}>
        {trigger}
      </div>
      {isOpen && ReactDOM.createPortal(
        <DropdownContext.Provider value={closeDropdown}>
          <SubMenuRefsContext.Provider value={subMenuRefsStore.current}>
            <div
              ref={dropdownRef}
              style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                zIndex: 2147483647,
                pointerEvents: 'auto',
                maxHeight: 'calc(100vh - 20px)',
                overflowY: 'auto',
              }}
              className="min-w-[180px] bg-[#1a1a2e] border border-white/10 rounded-lg shadow-2xl py-1"
              onClick={(e) => e.stopPropagation()}
            >
              {children}
            </div>
          </SubMenuRefsContext.Provider>
        </DropdownContext.Provider>,
        document.body
      )}
    </div>
  );
};

const DropdownItem = ({ children, onClick, className = '', disabled = false }) => {
  const closeDropdown = React.useContext(DropdownContext);

  const handleClick = (e) => {
    e.stopPropagation();
    onClick?.(e);
    closeDropdown?.();
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
};

const DropdownSeparator = () => (
  <div className="my-1 border-t border-white/10" />
);

// SubMenu component for "Move to" functionality - uses portal to escape overflow
const SubMenu = ({ trigger, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const submenuRef = useRef(null);
  const closeTimerRef = useRef(null);
  const parentCloseDropdown = React.useContext(DropdownContext);
  const subMenuRefsStore = React.useContext(SubMenuRefsContext);

  // Register submenu ref with parent dropdown so clicks inside aren't treated as "outside"
  useEffect(() => {
    const store = subMenuRefsStore;
    if (store) {
      store.refs.push(submenuRef);
      return () => {
        store.refs = store.refs.filter(r => r !== submenuRef);
      };
    }
  }, []);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  const calculatePosition = () => {
    if (!triggerRef.current) return { top: 0, left: 0 };
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuWidth = 160;
    const menuHeight = submenuRef.current ? submenuRef.current.offsetHeight : 200;
    const padding = 10;

    // Prefer right side, flip to left if no space
    let left = rect.right + 4;
    if (left + menuWidth + padding > viewportWidth) {
      left = rect.left - menuWidth - 4;
    }
    left = Math.max(padding, left);

    // Align top with trigger, clamp to viewport
    let top = rect.top;
    if (top + menuHeight + padding > viewportHeight) {
      top = viewportHeight - menuHeight - padding;
    }
    top = Math.max(padding, top);

    return { top, left };
  };

  useEffect(() => {
    if (isOpen) {
      const frame = requestAnimationFrame(() => {
        setPosition(calculatePosition());
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [isOpen, children]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => cancelClose();
  }, []);

  // Close both submenu and parent dropdown
  const closeAll = () => {
    setIsOpen(false);
    parentCloseDropdown?.();
  };

  return (
    <div
      ref={triggerRef}
      onMouseEnter={() => { cancelClose(); setIsOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      <div className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors cursor-pointer">
        {trigger}
        <ChevronRight className="w-4 h-4" />
      </div>
      {isOpen && ReactDOM.createPortal(
        <DropdownContext.Provider value={closeAll}>
          <div
            ref={submenuRef}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              zIndex: 2147483647,
              pointerEvents: 'auto',
            }}
            className="min-w-[160px] bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 max-h-[calc(100vh-20px)] overflow-y-auto"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            {children}
          </div>
        </DropdownContext.Provider>,
        document.body
      )}
    </div>
  );
};

export default function ElementsPanel({
  folders = [],
  elements = [],
  sharedFolders = [],
  activeElements = {},
  onActivateElement,
  onDeactivateElement,
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
  onCreateElement,
  onEditElement,
  onDuplicateElement,
  onDeleteElement,
  onSetDefault,
  hiddenTypesExtra = [],
  onMoveElement,
  onToggleFavorite,
  onPreviewVideo,
  onPreviewAudio,
  onPreviewPresentation,
  onRenameElement,
  onSharePresentation,
  onReorderElements,
  onReorderFolders,
  onOpenAssetLibrary,
  onCopySharedElement,
  onCloneAllSharedElements,
  onShareWithTeam,
  onShareElement,
  onShareFolder,
  onGetSharedElement,
  // Customizable label so the in-meeting sidebar can use the shorter
  // "Add Shared" without wrapping. Dashboard keeps the full label.
  getSharedLabel = 'Add Shared Elements',
  focusFolderId,
  onFocusFolderHandled,
}) {
  const { user } = useSiteContext();
  const isBasicPlan = user?.plan !== "Business";
  const isModeratorPlan = user?.plan === "co-host";
  const isNonSubscriber = user?.plan === "host" || user?.plan === "co-host";
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFolders, setExpandedFolders] = useState({});
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [localElementOrder, setLocalElementOrder] = useState(null);
  const [localFolderOrder, setLocalFolderOrder] = useState(null);
  const [isDraggingFolder, setIsDraggingFolder] = useState(false);
  const [renamingElement, setRenamingElement] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [showAddElementMenu, setShowAddElementMenu] = useState(false);
  const [addElementMenuPosition, setAddElementMenuPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 320, openUpward: false });
  const addElementRef = useRef(null);
  const addElementMenuRef = useRef(null);
  const folderRefs = useRef({});

  // When a folder is focused (e.g. after copying an element), expand it and scroll to it
  // Depends on both focusFolderId and folders so it works for newly created folders
  useEffect(() => {
    if (!focusFolderId) return;
    // Wait until the folder exists in the current list (may not be loaded yet for new folders)
    const folderExists = folders.some(f => f.id === focusFolderId || f._id === focusFolderId);
    if (!folderExists) return;
    // Expand the folder
    setExpandedFolders(prev => ({ ...prev, [focusFolderId]: true }));
    // Scroll to it after a short delay to allow re-render
    const timer = setTimeout(() => {
      const el = folderRefs.current[focusFolderId];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Brief highlight flash
        el.style.transition = 'box-shadow 0.3s ease';
        el.style.boxShadow = '0 0 0 2px rgba(106, 31, 191, 0.6)';
        setTimeout(() => { el.style.boxShadow = ''; }, 1500);
      }
      onFocusFolderHandled?.();
    }, 300);
    return () => clearTimeout(timer);
  }, [focusFolderId, folders]);

  // Close add element menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addElementRef.current && !addElementRef.current.contains(event.target) &&
          addElementMenuRef.current && !addElementMenuRef.current.contains(event.target)) {
        setShowAddElementMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update add element menu position when opened. The "Add Element" button
  // sits at the bottom of the panel, so opening downward usually extends past
  // the viewport. Calculate space below + above and either cap the height to
  // the visible area or flip upward when there's more room there.
  useEffect(() => {
    if (showAddElementMenu && addElementRef.current) {
      const rect = addElementRef.current.getBoundingClientRect();
      const GAP = 8;
      const VIEWPORT_PADDING = 16;
      const spaceBelow = window.innerHeight - rect.bottom - GAP - VIEWPORT_PADDING;
      const spaceAbove = rect.top - GAP - VIEWPORT_PADDING;
      const naturalHeight = 320; // matches the previous max-h-80 default
      const openUpward = spaceBelow < naturalHeight && spaceAbove > spaceBelow;
      const maxHeight = Math.max(160, openUpward ? spaceAbove : spaceBelow);
      setAddElementMenuPosition({
        top: openUpward ? null : rect.bottom + GAP,
        bottom: openUpward ? window.innerHeight - rect.top + GAP : null,
        left: rect.left,
        width: rect.width,
        maxHeight,
        openUpward,
      });
    }
  }, [showAddElementMenu]);

  const handleRenameElement = (element) => {
    setRenamingElement(element.id);
    setRenameValue(element.name);
  };

  const handleRenameSubmit = (elementId) => {
    const element = elements.find(el => el.id === elementId);
    if (element && renameValue.trim() && renameValue !== element.name) {
      onRenameElement?.(element, renameValue.trim());
    }
    setRenamingElement(null);
    setRenameValue('');
  };

  const handleRenameCancel = () => {
    setRenamingElement(null);
    setRenameValue('');
  };

  // Group elements by folder and sort by order
  const elementsByFolder = useMemo(() => {
    const grouped = {};
    folders.forEach(folder => {
      grouped[folder.id] = [];
    });

    elements.forEach(element => {
      if (grouped[element.folder_id]) {
        grouped[element.folder_id].push(element);
      }
    });

    // Sort each folder's elements by order
    Object.keys(grouped).forEach(folderId => {
      grouped[folderId].sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    return grouped;
  }, [folders, elements]);

  // Clear local order when elements prop changes (after successful save)
  useEffect(() => {
    if (localElementOrder) {
      const folderId = localElementOrder.folderId;
      const localIds = localElementOrder.elements.map(e => e.id);
      const propsIds = elements
        .filter(e => e.folder_id === folderId)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(e => e.id);

      if (JSON.stringify(localIds) === JSON.stringify(propsIds)) {
        setLocalElementOrder(null);
      }
    }
  }, [elements, localElementOrder]);

  // Clear local folder order when folders prop updates with new order
  useEffect(() => {
    if (localFolderOrder) {
      setLocalFolderOrder(null);
    }
  }, [folders]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragStart = (start) => {
    if (start.type === 'FOLDER') {
      setIsDraggingFolder(true);
    }
  };

  // Handle drag end for reordering (elements within folders, or folders themselves)
  const handleDragEnd = (result) => {
    setIsDraggingFolder(false);
    const { destination, source, draggableId, type } = result;

    if (!destination) {
      setLocalElementOrder(null);
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      setLocalElementOrder(null);
      return;
    }

    // Handle folder reordering
    if (type === 'FOLDER') {
      const sortedFolders = [...folders].sort((a, b) => (a.order || 0) - (b.order || 0));
      const [moved] = sortedFolders.splice(source.index, 1);
      sortedFolders.splice(destination.index, 0, moved);

      // Optimistic update — store reordered folders locally
      setLocalFolderOrder(sortedFolders.map((f, index) => ({ ...f, order: index })));

      const reordered = sortedFolders.map((f, index) => ({
        id: f.id,
        order: index
      }));

      onReorderFolders?.(reordered);
      return;
    }

    // Handle element reordering within a folder
    const folderId = source.droppableId;
    const folderElements = [...(elementsByFolder[folderId] || [])];

    const draggedElement = folderElements.find(el => el.id === draggableId);
    if (!draggedElement) {
      setLocalElementOrder(null);
      return;
    }

    folderElements.splice(source.index, 1);
    folderElements.splice(destination.index, 0, draggedElement);

    setLocalElementOrder({
      folderId,
      elements: folderElements
    });

    const reorderedElements = folderElements.map((el, index) => ({
      id: el.id,
      order: index
    }));

    onReorderElements?.(reorderedElements);
  };

  // Merge caller-supplied hidden types (e.g. admin-gated feature flags) with
  // the built-in hidden list. Product Showcase uses this to stay invisible
  // to non-admins while the feature is in testing.
  const effectiveHiddenTypes = useMemo(
    () => Array.from(new Set([
      ...HIDDEN_ELEMENT_TYPES,
      ...(hiddenTypesExtra || []),
      // Co-host plan moderators don't manage lead-capture forms in elements.
      ...(isModeratorPlan ? ['lead_capture', 'leadcapture'] : []),
    ])),
    [hiddenTypesExtra, isModeratorPlan]
  );

  // Filter elements based on search and favorites
  const filteredElements = useMemo(() => {
    let filtered = elements.filter(el => !effectiveHiddenTypes.includes(el.type));

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(el =>
        el.name.toLowerCase().includes(query) ||
        ELEMENT_LABELS[el.type]?.toLowerCase().includes(query)
      );
    }

    if (showFavoritesOnly) {
      filtered = filtered.filter(el => el.is_favorite);
    }

    return filtered;
  }, [elements, searchQuery, showFavoritesOnly, effectiveHiddenTypes]);

  // Get filtered elements by folder
  const getFilteredElementsForFolder = (folderId) => {
    if (localElementOrder && localElementOrder.folderId === folderId) {
      const folderEls = localElementOrder.elements;
      if (!searchQuery && !showFavoritesOnly) {
        return folderEls;
      }
      return folderEls.filter(el => filteredElements.some(f => f.id === el.id));
    }

    const folderEls = elementsByFolder[folderId] || [];
    if (!searchQuery && !showFavoritesOnly) {
      return folderEls;
    }
    return folderEls.filter(el => filteredElements.some(f => f.id === el.id));
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !(prev[folderId] ?? false),
    }));
  };

  const isElementActive = (element) => {
    return activeElements[element.type]?.id === element.id;
  };

  const startPresentation = (element) => {
    onActivateElement?.(element);
  };

  const renderElement = (element, index, isDragging = false, dragHandleProps = null) => {
    const Icon = ELEMENT_ICONS[element.type] || FileText;
    const colorClass = ELEMENT_COLORS[element.type] || 'text-gray-400';
    const isActive = isElementActive(element);
    const isRenaming = renamingElement === element.id;

    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (isRenaming) return;

      if (element.type === 'presentation') {
        if (isActive) {
          onDeactivateElement?.(element);
        } else {
          startPresentation(element);
        }
        return;
      }

      if (isActive) {
        onDeactivateElement?.(element);
      } else {
        onActivateElement?.(element);
      }
    };

    return (
      <div
        role="button"
        tabIndex={0}
        data-element-type={element.type}
        data-element-id={element.id}
        className={`flex items-center gap-1 py-1.5 px-1 rounded-lg cursor-pointer select-none ${
          isDragging ? 'shadow-lg bg-white/10 !transition-none' : 'transition-colors'
        } ${
          isActive
            ? 'bg-[#6a1fbf] text-white'
            : 'hover:bg-white/5 text-gray-300'
        }`}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        {!element.shared && (
          <div {...(dragHandleProps || {})} className="flex items-center flex-shrink-0 cursor-grab py-1 pr-1">
            <GripVertical className="w-3 h-3 text-gray-500" />
          </div>
        )}
        <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-white' : colorClass}`} />

        {isRenaming ? (
          <input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={() => handleRenameSubmit(element.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameSubmit(element.id);
              if (e.key === 'Escape') handleRenameCancel();
              e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            className="flex-1 text-sm bg-white/10 border border-white/20 rounded px-1.5 py-0.5 text-white outline-none focus:border-[#6a1fbf]"
          />
        ) : (
          <span className="flex-1 text-sm truncate min-w-0">{element.name}</span>
        )}

        {element.is_favorite && (
          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0" />
        )}
        {element.isDefault && (element.type === 'productshowcase' || element.type === 'product_showcase') && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#00c853]/20 text-[#00c853] flex-shrink-0">
            Default
          </span>
        )}

        <Dropdown
          trigger={
            <button
              className="p-1 rounded hover:bg-white/20 flex-shrink-0"
            >
              <MoreVertical className="w-3.5 h-3.5 text-gray-400" />
            </button>
          }
        >
          {element.shared ? (
            // Shared element — read-only context menu
            <>
              <DropdownItem onClick={() => onCopySharedElement?.(element)}>
                <Copy className="w-4 h-4 text-purple-400" />
                <span className="text-purple-200 font-medium">Copy to My Elements</span>
              </DropdownItem>
              <DropdownSeparator />
              <DropdownItem onClick={() => onEditElement?.(element)}>
                <Eye className="w-4 h-4" />
                View Details
              </DropdownItem>
              {element.type === 'video' && (
                <DropdownItem onClick={() => onPreviewVideo?.(element.data)}>
                  <Play className="w-4 h-4" />
                  Preview
                </DropdownItem>
              )}
              {element.type === 'audio' && (
                <DropdownItem onClick={() => onPreviewAudio?.(element.data)}>
                  <Play className="w-4 h-4" />
                  Preview
                </DropdownItem>
              )}
              {element.type === 'presentation' && (
                <DropdownItem onClick={() => onPreviewPresentation?.(element.data)}>
                  <Eye className="w-4 h-4" />
                  Preview
                </DropdownItem>
              )}
              {element.type === 'prompter' && (
                <DropdownItem onClick={() => onActivateElement?.(element)}>
                  <Play className="w-4 h-4" />
                  Open Teleprompter
                </DropdownItem>
              )}
            </>
          ) : (
            // Normal element — full context menu
            <>
              <DropdownItem onClick={() => handleRenameElement(element)}>
                <Pencil className="w-4 h-4" />
                Rename
              </DropdownItem>
              <DropdownItem onClick={() => onEditElement?.(element)}>
                <Edit className="w-4 h-4" />
                Edit
              </DropdownItem>
              {element.type === 'video' && (
                <DropdownItem onClick={() => onPreviewVideo?.(element.data)}>
                  <Eye className="w-4 h-4" />
                  Preview
                </DropdownItem>
              )}
              {element.type === 'audio' && (
                <DropdownItem onClick={() => onPreviewAudio?.(element.data)}>
                  <Play className="w-4 h-4" />
                  Preview
                </DropdownItem>
              )}
              {element.type === 'presentation' && (
                <>
                  <DropdownItem onClick={() => onPreviewPresentation?.(element.data)}>
                    <Eye className="w-4 h-4" />
                    Preview
                  </DropdownItem>
                  <DropdownItem onClick={() => onSharePresentation?.(element)}>
                    <Share2 className="w-4 h-4" />
                    Share
                  </DropdownItem>
                </>
              )}
              {element.type === 'prompter' && (
                <DropdownItem onClick={() => onActivateElement?.(element)}>
                  <Play className="w-4 h-4" />
                  Open Teleprompter
                </DropdownItem>
              )}
              <DropdownItem onClick={() => onDuplicateElement?.(element)}>
                <Copy className="w-4 h-4" />
                Duplicate
              </DropdownItem>
              <DropdownItem onClick={() => onToggleFavorite?.(element)}>
                <Star className="w-4 h-4" />
                {element.is_favorite ? 'Unfavorite' : 'Favorite'}
              </DropdownItem>
              {/* Share with Team is part of the Team / Users soft-launch —
                  restricted to internal admins (User.isAdmin === true) until
                  the feature rolls out to all Business subscribers. */}
              {user?.isAdmin && onShareWithTeam && (
                <DropdownItem onClick={() => onShareWithTeam?.(element)}>
                  <Share2 className="w-4 h-4" />
                  Share with Team
                  {Array.isArray(element.sharedWith) && element.sharedWith.length > 0 && (
                    <span className="ml-auto text-xs text-[#00c853]">
                      {element.sharedWith.length}
                    </span>
                  )}
                </DropdownItem>
              )}
              {(element.type === 'productshowcase' || element.type === 'product_showcase') && onSetDefault && (
                <DropdownItem onClick={() => onSetDefault?.(element)}>
                  <Star className="w-4 h-4 text-[#00c853]" />
                  {element.isDefault ? 'Remove as Default' : 'Set as Default'}
                </DropdownItem>
              )}
              {onShareElement && (
                <DropdownItem onClick={() => onShareElement?.(element)}>
                  <Share2 className="w-4 h-4 text-purple-400" />
                  <span className="text-purple-200">Share…</span>
                </DropdownItem>
              )}
              <DropdownSeparator />
              <SubMenu trigger={<><FolderInput className="w-4 h-4" /> Move to</>}>
                {folders.map(folder => (
                  <DropdownItem
                    key={folder.id}
                    onClick={() => onMoveElement?.(element, folder.id)}
                    disabled={folder.id === element.folder_id}
                  >
                    <FolderOpen className="w-4 h-4" />
                    {folder.name}
                    {folder.id === element.folder_id && <span className="ml-auto text-xs text-gray-500">Current</span>}
                  </DropdownItem>
                ))}
              </SubMenu>
              <DropdownSeparator />
              <DropdownItem
                onClick={() => onDeleteElement?.(element)}
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownItem>
            </>
          )}
        </Dropdown>
      </div>
    );
  };

  const renderFolder = (folder, folderDragHandleProps, isFolderDragging) => {
    const folderElements = getFilteredElementsForFolder(folder.id);
    const isExpanded = expandedFolders[folder.id] ?? false;
    const hasActiveElement = folderElements.some(el => isElementActive(el));

    const folderColor = folder.color || '#6a1fbf';

    // Collapse all folder content while any folder is being dragged for smooth reordering
    const showContent = isExpanded && !isDraggingFolder;

    return (
      <div key={folder.id} className={`mb-2 ${isFolderDragging ? 'opacity-90 shadow-xl rounded-lg' : ''}`} ref={el => { folderRefs.current[folder.id] = el; }}>
        <div
          className={`group flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-colors ${
            hasActiveElement ? 'ring-1 ring-white/30' : 'hover:brightness-110'
          }`}
          style={{ backgroundColor: `${folderColor}25` }}
          onClick={() => toggleFolder(folder.id)}
        >
          {/* Folder drag handle */}
          <div
            {...folderDragHandleProps}
            className="p-0.5 cursor-grab active:cursor-grabbing"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-3.5 h-3.5 text-gray-400" />
          </div>

          <button className="p-0.5">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-300" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-300" />
            )}
          </button>

          <FolderOpen
            className="w-4 h-4"
            style={{ color: folderColor }}
          />

          <span className="flex-1 text-sm font-medium text-white truncate">
            {folder.name}
          </span>

          <span
            className="text-xs text-white/80 px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${folderColor}40` }}
          >
            {folderElements.length}
          </span>

          <Dropdown
            trigger={
              <button
                className="p-1 rounded hover:bg-white/10"
              >
                <MoreVertical className="w-3.5 h-3.5 text-gray-400" />
              </button>
            }
          >
            <DropdownItem onClick={() => onEditFolder?.(folder)}>
              <Edit className="w-4 h-4" />
              Rename
            </DropdownItem>
            {onShareFolder && (
              <DropdownItem onClick={() => onShareFolder?.(folder)}>
                <Share2 className="w-4 h-4 text-purple-400" />
                <span className="text-purple-200">Share…</span>
              </DropdownItem>
            )}
            <DropdownSeparator />
            <DropdownItem
              onClick={() => onDeleteFolder?.(folder)}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </DropdownItem>
          </Dropdown>
        </div>

        <AnimatePresence>
          {showContent && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-1 pl-6"
            >
              <Droppable droppableId={folder.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`space-y-0.5 min-h-[20px] rounded-lg transition-colors ${
                      snapshot.isDraggingOver ? 'bg-white/5' : ''
                    }`}
                  >
                    {folderElements.length > 0 ? (
                      folderElements.map((element, index) => (
                        <Draggable key={element.id} draggableId={element.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                userSelect: 'none',
                              }}
                            >
                              {renderElement(element, index, snapshot.isDragging, provided.dragHandleProps)}
                            </div>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <p className="text-xs text-gray-500 px-3 py-2">No elements</p>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Render a shared (read-only) folder and its elements
  const renderSharedFolder = (folder) => {
    const isExpanded = expandedFolders[folder.id] ?? false;
    const folderElements = (folder.elements || []).filter(el => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return el.name.toLowerCase().includes(q) || ELEMENT_LABELS[el.type]?.toLowerCase().includes(q);
      }
      return true;
    });

    return (
      <div key={folder.id} className="mb-2">
        <div
          className="group flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-colors border border-purple-500/20 bg-purple-500/5 hover:bg-purple-500/10"
          onClick={() => toggleFolder(folder.id)}
        >
          <button className="p-0.5">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-purple-300" />
            ) : (
              <ChevronRight className="w-4 h-4 text-purple-300" />
            )}
          </button>
          <Lock className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-white truncate block">{folder.name}</span>
            {folder.ownerName && (
              <span className="text-xs text-purple-400">by {folder.ownerName}</span>
            )}
          </div>
          <span className="text-xs text-purple-300 px-1.5 py-0.5 rounded bg-purple-500/20 flex-shrink-0">
            Shared
          </span>
          <span className="text-xs text-white/60 px-1.5 py-0.5 rounded bg-white/10 flex-shrink-0">
            {folderElements.length}
          </span>
          {/* Clone All button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCloneAllSharedElements?.(folder);
            }}
            className="p-1 rounded hover:bg-purple-500/30 transition-colors flex-shrink-0"
            title="Clone all elements to my library"
          >
            <Copy className="w-3.5 h-3.5 text-purple-300" />
          </button>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-1 pl-6"
            >
              <div className="space-y-0.5 min-h-[20px]">
                {folderElements.length > 0 ? (
                  [...folderElements].sort((a, b) => {
                    if (isBasicPlan) {
                      const aAllowed = BASIC_PLAN_TYPES.includes(a.type);
                      const bAllowed = BASIC_PLAN_TYPES.includes(b.type);
                      if (aAllowed && !bAllowed) return -1;
                      if (!aAllowed && bAllowed) return 1;
                    }
                    return 0;
                  }).map((element) => {
                    const Icon = ELEMENT_ICONS[element.type] || FileText;
                    const colorClass = ELEMENT_COLORS[element.type] || 'text-gray-400';
                    const isActive = isElementActive(element);
                    const isGatedElement = isBasicPlan && !BASIC_PLAN_TYPES.includes(element.type);
                    return (
                      <div
                        key={element.id}
                        role="button"
                        tabIndex={0}
                        className={`flex items-center gap-1 py-1.5 px-1 rounded-lg select-none transition-colors ${
                          isActive ? 'bg-[#6a1fbf] text-white cursor-pointer' : 'hover:bg-white/5 text-gray-300 cursor-pointer'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          isActive ? onDeactivateElement?.(element) : onActivateElement?.(element);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            isActive ? onDeactivateElement?.(element) : onActivateElement?.(element);
                          }
                        }}
                      >
                        {/* No drag handle for shared elements */}
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-white' : colorClass}`} />
                        <span className="flex-1 text-sm truncate min-w-0">{element.name}</span>
                        <Dropdown
                          trigger={
                            <button className="p-1 rounded hover:bg-white/20 flex-shrink-0">
                              <MoreVertical className="w-3.5 h-3.5 text-gray-400" />
                            </button>
                          }
                        >
                          {!isGatedElement && (
                            <>
                              <DropdownItem onClick={() => onCopySharedElement?.(element)}>
                                <Copy className="w-4 h-4 text-purple-400" />
                                <span className="text-purple-200 font-medium">Copy to My Elements</span>
                              </DropdownItem>
                              <DropdownSeparator />
                            </>
                          )}
                          <DropdownItem onClick={() => onEditElement?.(element)}>
                            <Eye className="w-4 h-4" />
                            View Details
                          </DropdownItem>
                          {element.type === 'video' && (
                            <DropdownItem onClick={() => onPreviewVideo?.(element.data)}>
                              <Play className="w-4 h-4" />
                              Preview
                            </DropdownItem>
                          )}
                          {element.type === 'audio' && (
                            <DropdownItem onClick={() => onPreviewAudio?.(element.data)}>
                              <Play className="w-4 h-4" />
                              Preview
                            </DropdownItem>
                          )}
                          {element.type === 'presentation' && (
                            <DropdownItem onClick={() => onPreviewPresentation?.(element.data)}>
                              <Eye className="w-4 h-4" />
                              Preview
                            </DropdownItem>
                          )}
                          {element.type === 'prompter' && (
                            <DropdownItem onClick={() => onActivateElement?.(element)}>
                              <Play className="w-4 h-4" />
                              Open Teleprompter
                            </DropdownItem>
                          )}
                        </Dropdown>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-xs text-gray-500 px-3 py-2">No elements</p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top Action Buttons */}
      {onOpenAssetLibrary && (
        <div className="px-3 pt-3 space-y-2">
          <button
            onClick={onOpenAssetLibrary}
            className="w-full flex items-center justify-center gap-2 p-2.5 bg-gradient-to-r from-[#6a1fbf]/20 to-[#00c853]/20 hover:from-[#6a1fbf]/30 hover:to-[#00c853]/30 border border-[#6a1fbf]/30 rounded-lg transition-colors group"
          >
            <FolderHeart className="w-4 h-4 text-[#6a1fbf] group-hover:text-[#7a2fcf]" />
            <span className="text-sm font-medium text-white">Asset Library</span>
          </button>
        </div>
      )}

      {/* Search & Filters */}
      <div className="p-3 space-y-2 border-b border-white/10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search elements..."
            className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-gray-500 hover:text-white" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
              showFavoritesOnly
                ? 'bg-yellow-500/20 text-yellow-400'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <Star className={`w-3.5 h-3.5 ${showFavoritesOnly ? 'fill-current' : ''}`} />
            Favorites
          </button>

          <div className="flex-1" />

          {onGetSharedElement && (
            <button
              onClick={onGetSharedElement}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-purple-200 bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/30 rounded-lg transition-colors whitespace-nowrap"
              title="Import an element or folder someone shared with you using a code"
            >
              <Download className="w-3.5 h-3.5" />
              {getSharedLabel}
            </button>
          )}

          <button
            onClick={onCreateFolder}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Folder
          </button>
        </div>

        {/* Add Element Button */}
        <div className="relative" ref={addElementRef}>
          <button
            onClick={() => setShowAddElementMenu(!showAddElementMenu)}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:from-[#7a2fcf] hover:to-[#00d863] rounded-lg text-white font-medium transition-all"
          >
            <Plus className="w-4 h-4" />
            Add Element
          </button>

          {showAddElementMenu && ReactDOM.createPortal(
            <div
              ref={addElementMenuRef}
              style={{
                position: 'fixed',
                ...(addElementMenuPosition.top != null ? { top: addElementMenuPosition.top } : {}),
                ...(addElementMenuPosition.bottom != null ? { bottom: addElementMenuPosition.bottom } : {}),
                left: addElementMenuPosition.left,
                width: addElementMenuPosition.width,
                maxHeight: addElementMenuPosition.maxHeight,
                zIndex: 99999,
              }}
              className="bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 overflow-y-auto"
            >
              {folders.length === 0 ? (
                <div className="px-3 py-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-yellow-300 font-medium">No folders yet</p>
                    <p className="text-xs text-gray-400 mt-0.5">Create a folder first to organize your elements.</p>
                    <button
                      onClick={() => {
                        onCreateFolder?.();
                        setShowAddElementMenu(false);
                      }}
                      className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-[#6a1fbf] hover:bg-[#7a2fcf] text-white text-xs rounded-lg transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      Create Folder
                    </button>
                  </div>
                </div>
              ) : (
                Object.entries(ELEMENT_LABELS)
                  .filter(([type]) => !effectiveHiddenTypes.includes(type))
                  .map(([type, label]) => {
                  const Icon = ELEMENT_ICONS[type];
                  const colorClass = ELEMENT_COLORS[type];
                  const isGated = isBasicPlan && !BASIC_PLAN_TYPES.includes(type);
                  return (
                    <button
                      key={type}
                      onClick={() => {
                        if (isGated) return;
                        onCreateElement?.(type);
                        setShowAddElementMenu(false);
                      }}
                      disabled={isGated}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isGated ? "text-gray-600 opacity-50 cursor-not-allowed" : "text-gray-300 hover:bg-white/10 hover:text-white"}`}
                    >
                      <Icon className={`w-4 h-4 ${isGated ? "text-gray-600" : colorClass}`} />
                      {label}
                    </button>
                  );
                })
              )}
            </div>,
            document.body
          )}
        </div>
      </div>

      {/* Folders & Elements */}
      <div className="flex-1 overflow-y-auto custom-purple-scroll">
        <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="p-2">
            {folders.length > 0 ? (
              <Droppable droppableId="folders-list" type="FOLDER">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {(localFolderOrder || [...folders].sort((a, b) => (a.order || 0) - (b.order || 0)))
                      .map((folder, index) => (
                        <Draggable key={folder.id} draggableId={`folder-${folder.id}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                userSelect: 'none',
                              }}
                            >
                              {renderFolder(folder, provided.dragHandleProps, snapshot.isDragging)}
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            ) : (
              <div className="text-center py-8">
                <FolderOpen className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No folders yet</p>
                <button
                  onClick={onCreateFolder}
                  className="mt-3 flex items-center gap-1 px-4 py-2 mx-auto bg-[#6a1fbf] hover:bg-[#7a2fcf] text-white text-sm rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Folder
                </button>
              </div>
            )}
          </div>
        </DragDropContext>

        {/* Shared Libraries Section */}
        {sharedFolders.length > 0 && (
          <div className="px-2 pb-2">
            <div className="flex items-center gap-2 px-1 py-2 mt-1">
              <Library className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-xs font-semibold text-purple-300 uppercase tracking-wider">Shared Libraries</span>
            </div>
            {sharedFolders.map(folder => renderSharedFolder(folder))}
          </div>
        )}
      </div>

      <style>{`
        .custom-purple-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .custom-purple-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-purple-scroll::-webkit-scrollbar-thumb {
          background: #6a1fbf;
          border-radius: 4px;
        }
        .custom-purple-scroll::-webkit-scrollbar-thumb:hover {
          background: #8b3fd6;
        }
      `}</style>
    </div>
  );
}
